import { appUtils } from 'lib/AppUtils'
import { MutableRefObject, useCallback, useEffect, useRef } from 'react'
import { mutate, MutatorOptions } from 'swr'

import { Action0 } from '@/utils/Types'

export function useMounted() {
  const isMounted = useRef(false)
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])
  return isMounted
}

export const useRenderCounter = label => {
  const counter = useRef(0)
  counter.current++
  console.log(`RENDER COUNTER: ${label} rendered ${counter.current} times`)
}

/**
 * 锁住页面滚动。**只给占满屏幕的东西用**（模态框、全屏遮罩）。
 *
 * 内联控件（紧贴着输入框往下长的下拉列表）不要用这个 —— 列表落在视口外时，
 * 用户连滚下去看选项都做不到，表现是「点了没反应」。那种情况用 useScrollIntoView。
 * 2026-11-03 查出来的爱尔兰选不了，根因就是 SearchBarDropdown 误用了这个 hook。
 *
 * ⚠️ 下面那个 else 分支看着像冗余（关着的时候还去写 body），**先别删**。
 * hooks/Carousel.ts 的 handleTouchStart 直接把 body 锁成 hidden，而还原只挂在
 * handleTouchEnd 上 —— 全仓一处 onTouchCancel 都没有。滑 banner 时来个电话、
 * 系统手势或浏览器打断，触发的是 touchcancel，锁就留在 body 上没人解。
 * 今天它能自愈，靠的正是这里：任意一个 useScrollable(false)（UserDropdown /
 * Modal / LoadingView / NavigationTab 挂载时都会跑）顺手把 overflow 写回 visible。
 *
 * 要改成「保存/还原进入前的值」（那样更正确，也不会解掉外层模态框的锁），
 * 必须先给 Carousel 补上 touchcancel 的还原，否则首页会从「偶发但自愈」
 * 变成「偶发且卡死」。2026-11-03 讨论记录在 #Rkings-研发部。
 */
export function useScrollable(scrollable: boolean) {
  useEffect(() => {
    if (scrollable) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'visible'
    }
    return () => {
      document.body.style.overflow = 'visible'
    }
  }, [scrollable])
}

/**
 * 展开后把元素滚进视口。给**内联**下拉列表用，替代 useScrollable。
 *
 * 列表是 absolute top-full，紧贴控件往下长：控件落在屏幕底部时（手机上往下填表很常见）
 * 整个列表都在视口外。只在确实露不全时滚，滚多少就是差多少 ——
 * 控件本来就在屏幕中间时不会无谓地跳一下。
 */
export function useScrollIntoView(active: boolean, ref: MutableRefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!active) return
    const el = ref.current
    if (!el) return
    const overflow = el.getBoundingClientRect().bottom - window.innerHeight
    if (overflow > 0) {
      window.scrollBy({ top: overflow + 8, behavior: 'smooth' })
    }
  }, [active, ref])
}

export function useClickOutside(ref: MutableRefObject<any>, fn: Action0) {
  const handleClickOutside = event => {
    if (ref.current && !ref.current.contains(event.target)) {
      fn()
    }
  }
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [])
}

export const useGlobalMutate = (targets: string | Array<string>, data?: any, opts?: boolean | MutatorOptions<any>) => {
  return useCallback(
    () =>
      mutate(
        key =>
          Array.isArray(key)
            ? !appUtils.isEmpty(key?.[0]) && (Array.isArray(targets) ? targets?.some(target => key[0].includes(target)) : key[0].includes(targets))
            : typeof key === 'string' && (Array.isArray(targets) ? targets?.some(target => key.includes(target)) : key.includes(targets)),
        data,
        opts
      ),
    [targets]
  )
}
